import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import styles from "./Transfer.module.scss";
import { ReactComponent as ContentCopy } from "../../assets/images/content-copy.svg";
import { useDispatch, useSelector } from "react-redux";
import Button from "../button/Button";
import {
  TrackOrderStatusAction,
  payOrderAction,
  trackOrderAction,
} from "../../redux/actions/orderStatus/orderStatusAction";
import { saveVirtualAccount } from "../../redux/actions/orderDetails/orderDetailsActions";
import PendingPaymentCard from "../PendingPaymentCard/PendingPaymentCard";
import { commarize, getMinutesLeft, setExpiryTime } from "../../utils/helpers";
import queryString from "query-string";
import { useTimerContext } from "../../hooks/use-timer-context";
import {
  openLoader,
  closeLoader,
} from "../../redux/actions/loader/loaderActions";
import transactpay from "../../assets/images/iconlogo.png";
import pattern from "../../assets/images/footer-pattern.svg";
import { getEnvironment } from "../../utils/getEnvironment";

// get env
// const environment = getEnvironment();
// const IS_SANDBOX = environment === "DEV";

type SandboxMode = "Success" | "Failed";

type TransferProps = {
  reference: string;
  isTestMode?: boolean;
};

const Transfer = ({ reference, isTestMode }: TransferProps) => {
  const {
    timeRemainingInMinutesText,
    startTimer,
    timeRemainingInSeconds,
    hasTimerStarted,
    resetTimer,
  } = useTimerContext();

  const dispatch = useDispatch();
  const { orderDetails, virtualAccount } = useSelector(
    (state) => state.orderDetailsReducer,
  );

  const [timeleft, setTimeleft] = useState<any>();

  const isSandbox = isTestMode;

  // Sandbox state — null means user hasn't picked yet
  const [sandboxMode, setSandboxMode] = useState<SandboxMode | null>(null);
  const [sandboxConfirmed, setSandboxConfirmed] = useState(false);

  // For sandbox: don't show skeleton until they confirm mode
  // For real: show skeleton until account loads
  const [isFetchingAccount, setIsFetchingAccount] = useState(
    isSandbox
      ? false
      : !(virtualAccount && virtualAccount.reference === reference),
  );

  const [accountdetails, setAccountDetails] = useState(() => {
    if (virtualAccount && virtualAccount.reference === reference) {
      return {
        recipientAccount: virtualAccount.recipientAccount || "",
        expirytime: virtualAccount.expirytime || null,
        bankName: virtualAccount.bankName || "",
        accountName: virtualAccount.accountName || "",
      };
    }
    return {
      recipientAccount: "",
      expirytime: null,
      bankName: "",
      accountName: "",
    };
  });

  const [componentData, setComponentData] = React.useState<any>({
    pendingfor5min: false,
  });

  const hasLoadedAccount = useRef(false);

  const [copied, setCopied] = useState(false);
  if (copied) {
    setTimeout(() => setCopied(false), 2000);
  }
  const [amountCopied, setamountCopied] = useState(false);
  if (amountCopied) {
    setTimeout(() => setamountCopied(false), 2000);
  }

  // Base payload — sandboxMetadata only present in sandbox mode
  // Status is driven by sandboxMode selection
  const payload = useMemo(
    () => ({
      reference: reference,
      paymentOption: "BANK-TRANSFER",
      bankTransfer: { bankCode: null },
      sandboxMetadata: isSandbox
        ? {
            bankTransferMetadata: {
              status: sandboxMode ?? "Success",
            },
          }
        : undefined,
    }),
    [reference, isSandbox, sandboxMode],
  );

  const updatePendingStatus = useCallback(() => {
    setComponentData((componentData: any) => ({
      ...componentData,
      pendingfor5min: true,
    }));
  }, []);

  const setTimerCounter = () => {
    const timer = setExpiryTime();
    setTimeleft({ expirytime: timer });
    setInterval(() => {
      setTimeleft({ expirytime: timer });
    }, 100);
  };

  useEffect(() => {
    setComponentData((data: any) => ({ ...data, pendingfor5min: false }));
    if (!virtualAccount || virtualAccount.reference !== reference) {
      startTimer(900);
    }
    return () => {};
  }, [startTimer, resetTimer, virtualAccount, reference]);

  useEffect(() => {
    if (timeRemainingInSeconds < 0) return;
    if (hasTimerStarted && timeRemainingInSeconds === 0) {
      updatePendingStatus();
    }
  }, [timeRemainingInSeconds, hasTimerStarted, updatePendingStatus]);

  // Real flow — runs on mount for non-sandbox only
  useEffect(() => {
    if (hasLoadedAccount.current) return;
    if (isSandbox) return; // sandbox waits for handleSandboxConfirm

    if (virtualAccount && virtualAccount.reference === reference) {
      hasLoadedAccount.current = true;
      setIsFetchingAccount(false);
      setTimerCounter();
      TrackOrderStatusAction(payload, "transfer", dispatch, () => {});
      dispatch(
        trackOrderAction(
          { eventname: "MADE-PAYMENT", orderReference: payload.reference },
          () => {},
        ),
      );
    } else {
      dispatch(openLoader("Generating Account Details", "default"));
      payOrderAction(payload, "default", dispatch, (data: any) => {
        const newAccountDetails = {
          recipientAccount:
            data?.data?.paymentDetail?.recipientAccount ||
            data?.data?.bankTransferDetails?.recipientAccount,
          expirytime:
            data?.data?.paymentDetail?.expirytime ||
            data?.data?.bankTransferDetails?.expirytime,
          bankName:
            data?.data?.paymentDetail?.bankName ||
            data?.data?.bankTransferDetails?.bankName,
          accountName:
            data?.data?.paymentDetail?.accountName ||
            data?.data?.bankTransferDetails?.accountName,
          reference: reference,
        };
        setAccountDetails(newAccountDetails);
        dispatch(saveVirtualAccount(newAccountDetails));
        dispatch(closeLoader());
        setIsFetchingAccount(false); // hide skeleton, show real data
        hasLoadedAccount.current = true;
        setTimerCounter();
        TrackOrderStatusAction(payload, "transfer", dispatch, () => {});
        dispatch(
          trackOrderAction(
            { eventname: "MADE-PAYMENT", orderReference: payload.reference },
            () => {},
          ),
        );
      });
    }
  }, [reference, virtualAccount, dispatch, payload]);

  // Sandbox confirm — user has picked Success or Failed, now kick off the same real flow
  const handleSandboxConfirm = () => {
    if (!sandboxMode) return;
    setSandboxConfirmed(true);
    setIsFetchingAccount(true);

    const sandboxPayload = {
      reference,
      paymentOption: "BANK-TRANSFER",
      bankTransfer: { bankCode: null },
      sandboxMetadata: {
        bankTransferMetadata: { status: sandboxMode },
      },
    };

    payOrderAction(sandboxPayload, "default", dispatch, (data: any) => {
      const newAccountDetails = {
        recipientAccount:
          data?.data?.paymentDetail?.recipientAccount ||
          data?.data?.bankTransferDetails?.recipientAccount,
        expirytime:
          data?.data?.paymentDetail?.expirytime ||
          data?.data?.bankTransferDetails?.expirytime,
        bankName:
          data?.data?.paymentDetail?.bankName ||
          data?.data?.bankTransferDetails?.bankName,
        accountName:
          data?.data?.paymentDetail?.accountName ||
          data?.data?.bankTransferDetails?.accountName,
        reference,
      };
      setAccountDetails(newAccountDetails);
      dispatch(saveVirtualAccount(newAccountDetails));
      dispatch(closeLoader());
      setIsFetchingAccount(false);
      hasLoadedAccount.current = true;
      setTimerCounter();
      // Same polling as normal flow
      TrackOrderStatusAction(sandboxPayload, "transfer", dispatch, () => {});
      dispatch(
        trackOrderAction(
          { eventname: "MADE-PAYMENT", orderReference: reference },
          () => {},
        ),
      );
    });
  };

  // Exactly the same as normal — polling already running from handleSandboxConfirm
  const MadePayment = () => {
    dispatch(openLoader("Checking Transaction Status", "transfer"));
    TrackOrderStatusAction(payload, "transfer", dispatch, () => {});
    dispatch(
      trackOrderAction(
        { eventname: "MADE-PAYMENT", orderReference: payload.reference },
        () => {},
      ),
    );
  };

  const merchantEmail =
    orderDetails?.data?.subsidiary?.email ??
    orderDetails?.data?.customer?.email;

  const bankName = accountdetails?.bankName;
  const accountName = accountdetails?.accountName;

  const body = queryString.parse(window.location.search);
  const orderfee = useSelector((state) => state.orderFeeReducer);
  const fee: number = orderfee?.data?.customerFee ?? 0;
  const currency = orderDetails?.data?.order?.currency;
  const amount = orderDetails?.data?.isDiscounted
    ? orderDetails?.data?.newAmount
    : orderDetails?.data?.order?.amount;

  return (
    <Fragment>
      {componentData?.pendingfor5min ? (
        <PendingPaymentCard paymentmethod="Transfer" />
      ) : (
        <>
          <div className={styles.transfer}>
            {/* Sandbox picker — shown before confirm */}
            {isSandbox && !sandboxConfirmed && (
              <>
                <div className={styles.sandboxWrapper}>
                  <p className={styles.sandboxTitle}>
                    Simulate transfer outcome
                  </p>
                  <div className={styles.sandboxOptions}>
                    {(["Success", "Failed"] as SandboxMode[]).map((mode) => (
                      <label
                        key={mode}
                        className={`${styles.sandboxOption} ${
                          sandboxMode === mode ? styles.active : ""
                        }`}
                        onClick={() => setSandboxMode(mode)}
                      >
                        <input
                          type="radio"
                          name="sandboxMode"
                          value={mode}
                          checked={sandboxMode === mode}
                          onChange={() => setSandboxMode(mode)}
                          className={styles.sandboxRadio}
                        />
                        {mode}
                      </label>
                    ))}
                  </div>
                </div>

                <button
                  className={`${styles.sandboxConfirmBtn} ${
                    !sandboxMode ? styles.disabled : ""
                  }`}
                  disabled={!sandboxMode}
                  onClick={handleSandboxConfirm}
                >
                  Continue
                </button>
              </>
            )}

            {/* Everything below hidden until sandbox confirmed (or not sandbox at all) */}
            {(!isSandbox || sandboxConfirmed) && (
              <>
                {/* Sandbox confirmed badge */}
                {isSandbox && sandboxConfirmed && (
                  <div className={styles.sandboxWrapper}>
                    <p className={styles.sandboxConfirmedText}>
                      Simulating: <strong>{sandboxMode}</strong>
                    </p>
                  </div>
                )}

                <div className={styles.transferbodycontainer}>
                  <span
                    className={styles.separatetextbox}
                    style={{ color: "#8E173E" }}
                  >
                    Please transfer the exact amount shown to avoid rejection.
                  </span>

                  {isFetchingAccount ? (
                    <div className={styles.transferbody}>
                      <style>{`
              @keyframes shimmer {
                0%   { background-position: 200% 0; }
                100% { background-position: -200% 0; }
              }
            `}</style>
                      {["Amount", "Account Number", "Bank name"].map(
                        (label) => (
                          <div key={label} className={styles.detailsWrapper}>
                            <p className={styles.account_label}>{label}</p>
                            <div
                              style={{
                                height: "18px",
                                width: "150px",
                                borderRadius: "6px",
                                background:
                                  "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
                                backgroundSize: "200% 100%",
                                animation: "shimmer 1.4s infinite",
                              }}
                            />
                          </div>
                        ),
                      )}
                    </div>
                  ) : (
                    <div className={styles.transferbody}>
                      <div className={styles.detailsWrapper}>
                        <div className={styles.account_label_text_Box}>
                          <p className={styles.account_label}>Amount</p>
                        </div>
                        <span className={styles.AmountNumber}>
                          {currency ?? "NGN"} {commarize((Number(amount) + fee).toFixed(10))}
                          <CopyToClipboard
                            text={`${commarize((Number(amount) + fee).toFixed(10))}`}
                            onCopy={() => setamountCopied(true)}
                          >
                            <span className={styles.copy}>
                              {amountCopied ? (
                                "Copied!"
                              ) : (
                                <ContentCopy style={{ height: "17px" }} />
                              )}
                            </span>
                          </CopyToClipboard>
                        </span>
                      </div>

                      <div className={styles.detailsWrapper}>
                        <p className={styles.account_label}>Account Number</p>
                        <span className={styles.accountNumber}>
                          {accountdetails?.recipientAccount ?? "00000000000"}
                          <CopyToClipboard
                            text={accountdetails?.recipientAccount}
                            onCopy={() => setCopied(true)}
                          >
                            <span className={styles.copy}>
                              {copied ? (
                                "Copied!"
                              ) : (
                                <ContentCopy style={{ height: "17px" }} />
                              )}
                            </span>
                          </CopyToClipboard>
                        </span>
                      </div>

                      <div className={styles.detailsWrapper}>
                        <p className={styles.account_label}>Bank name</p>
                        <p style={{ fontWeight: "bolder" }}>
                          {bankName ?? "Wema Bank"}
                        </p>
                      </div>
                    </div>
                  )}

                  <span className={styles.separatetextbox}>
                    Account details are valid for this transaction only and will
                    expire in{" "}
                    <span
                      style={{
                        color: "#8E173E",
                        fontWeight: "600",
                        margin: "0 4px",
                      }}
                    >
                      {timeRemainingInMinutesText}
                    </span>{" "}
                    minutes
                  </span>
                </div>

                <img
                  src={pattern}
                  alt="pattern"
                  className={styles.mobilepattern}
                />

                <div className={styles.copy_to_clipboard}>
                  <Button
                    width="100%"
                    onClick={() => MadePayment()}
                    style={{
                      color: "#ffffff",
                      width: "100%",
                      backgroundColor: "#8E173E",
                    }}
                    value="I have sent the money"
                  />
                </div>
              </>
            )}
          </div>

          <div className={styles.poweredByBox}>
            <p className={styles.poweredBy}>Powered by</p>
            <img
              src={transactpay}
              style={{ width: "auto", height: "20px", objectFit: "cover" }}
              alt="Transactpay"
            />
          </div>
        </>
      )}
    </Fragment>
  );
};

export default Transfer;
